import React, { ReactElement, useMemo } from 'react';
import { Paper, Skeleton, Stack, Typography } from '@mui/material';

import { useGetMainTokenBalanceQuery } from 'modules/api/actions/getMainTokenBalance';
import { useGetMainTokenUSDRatioQuery } from 'modules/api/actions/getMainTokenUSDRatio';
import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { useConnection } from 'modules/auth/hooks/useConnection';
import {
  DECIMAL_PLACES_SHORT,
  DEFAULT_DECIMAL_PLACES,
  HUNDRED,
  ZERO,
} from 'modules/common/const';
import { useGetUSDAmount } from 'modules/common/hooks/useGetUSDAmount';
import { useGlobalTranslation } from 'modules/i18n';
import { useStakeToken } from 'modules/liquidStaking/hooks/useStakeToken';
import { useGetAccountDelegateTotalStaked } from 'modules/pool/hooks/useGetAccountDelegateTotalStaked';

import { Progress, ProgressLine } from '../Progress';
import { InfoItem } from './InfoItem';
import { translation } from './translation';
import { useStyles } from './useStyles';

export function TotalStaked(): ReactElement {
  const { classes } = useStyles();
  const { t, keys } = useGlobalTranslation(translation);

  const { isConnected, connectionKey, cacheTime } = useConnection();
  const {
    totalStakedAmount: delegateAmount,
    isLoading: isDelegateAmountLoading,
  } = useGetAccountDelegateTotalStaked();
  const { mainBalance: stakeMainBalance, isBalanceLoading } = useStakeToken();

  const {
    data: availableBalance = ZERO,
    isLoading: isAvailableBalanceLoading,
  } = useGetMainTokenBalanceQuery(
    { connectionKey },
    {
      skip: !isConnected,
      selectFromResult: mapDataToUndefinedIfSkip,
      refetchOnMountOrArgChange: cacheTime,
    },
  );
  const { isLoading: isMainRatioLoading } = useGetMainTokenUSDRatioQuery();

  const availableBalanceUSD = useGetUSDAmount(availableBalance);

  const totalStakedAmount = useMemo(
    () => delegateAmount.plus(stakeMainBalance),
    [delegateAmount, stakeMainBalance],
  );

  const totalStakedUsd = useGetUSDAmount(totalStakedAmount);

  const totalAmount = availableBalance.plus(totalStakedAmount);
  const token = t(keys.tokens.main);

  const delegateAmountPct = useMemo(
    () =>
      delegateAmount.isZero() || totalAmount.isZero()
        ? ZERO
        : delegateAmount.dividedBy(totalAmount).multipliedBy(HUNDRED),
    [delegateAmount, totalAmount],
  );

  const liquidAmountPct = useMemo(
    () =>
      stakeMainBalance.isZero() || totalAmount.isZero()
        ? ZERO
        : stakeMainBalance.dividedBy(totalAmount).multipliedBy(HUNDRED),
    [stakeMainBalance, totalAmount],
  );

  const isAmountLoading =
    isDelegateAmountLoading || isAvailableBalanceLoading || isBalanceLoading;

  const totalStakedUsdContent =
    !!totalStakedUsd &&
    t(keys.unit.$, {
      value: totalStakedUsd.toFormat(DECIMAL_PLACES_SHORT),
    });

  return (
    <Paper className={classes.root}>
      <Typography className={classes.title}>
        {t(keys.totalStaked, { token })}
      </Typography>

      <Typography className={classes.amount}>
        {isAmountLoading ? (
          <Skeleton width={90} />
        ) : (
          totalStakedAmount
            .decimalPlaces(DEFAULT_DECIMAL_PLACES)
            .toFormat(
              totalStakedAmount.isZero() ? DECIMAL_PLACES_SHORT : undefined,
            )
        )}
      </Typography>

      <Typography className={classes.amountUsd}>
        {isAmountLoading || isMainRatioLoading ? (
          <Skeleton width={40} />
        ) : (
          totalStakedUsdContent
        )}
      </Typography>

      <Progress
        isLoading={isAmountLoading}
        percent={delegateAmountPct.plus(liquidAmountPct).toNumber()}
        sx={{ mt: 8 }}
      >
        <ProgressLine
          percent={delegateAmount
            .div(totalStakedAmount)
            .multipliedBy(HUNDRED)
            .toNumber()}
        />
      </Progress>

      <Stack
        direction="row"
        flexWrap="wrap"
        sx={{ mt: 4, gap: theme => theme.spacing(3, 4.5) }}
      >
        <InfoItem
          color="primary"
          isLoading={isAmountLoading}
          label={t(keys.delegate)}
          value={t(keys.unit.percent, {
            value: delegateAmountPct
              .decimalPlaces(DECIMAL_PLACES_SHORT)
              .toFormat(),
          })}
        />

        <InfoItem
          color="secondary"
          isLoading={isAmountLoading}
          label={t(keys.liquid)}
          sx={{ mr: 'auto' }}
          value={t(keys.unit.percent, {
            value: liquidAmountPct
              .decimalPlaces(DECIMAL_PLACES_SHORT)
              .toFormat(),
          })}
        />

        <InfoItem
          additionalValue={
            availableBalanceUSD
              ? t(keys.unit.$, {
                  value: availableBalanceUSD.toFormat(DECIMAL_PLACES_SHORT),
                })
              : undefined
          }
          color="other"
          isLoading={isAmountLoading}
          label={t(keys.availableToStake, { token })}
          value={availableBalance
            .decimalPlaces(DEFAULT_DECIMAL_PLACES)
            .toFormat()}
        />
      </Stack>
    </Paper>
  );
}
